import { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import { permanentQrData, type PermanentQrEntry } from '@/mocks/superAdminPermanentQr';

type FilterKey = 'all' | 'has_domain' | 'no_domain' | 'custom_domain' | 'rfw_subdomain' | 'rfe_page';

export default function SAPermanentQrPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [previewQr, setPreviewQr] = useState<PermanentQrEntry | null>(null);

  const totalScans = useMemo(
    () => permanentQrData.reduce((sum, q) => sum + q.lastScanCount, 0),
    [],
  );
  const withDomain = useMemo(() => permanentQrData.filter((q) => q.hasDomain).length, []);
  const withoutDomain = useMemo(() => permanentQrData.filter((q) => !q.hasDomain).length, []);

  const filtered = useMemo(() => {
    let list = permanentQrData;
    if (search.trim()) {
      const lower = search.toLowerCase();
      list = list.filter(
        (q) =>
          q.radioName.toLowerCase().includes(lower) ||
          q.frequency.toLowerCase().includes(lower) ||
          q.redirectCode.toLowerCase().includes(lower) ||
          (q.currentDomain && q.currentDomain.toLowerCase().includes(lower)),
      );
    }
    switch (filter) {
      case 'has_domain':
        list = list.filter((q) => q.hasDomain);
        break;
      case 'no_domain':
        list = list.filter((q) => !q.hasDomain);
        break;
      case 'custom_domain':
        list = list.filter((q) => q.domainType === 'custom_domain');
        break;
      case 'rfw_subdomain':
        list = list.filter((q) => q.domainType === 'rfw_subdomain');
        break;
      case 'rfe_page':
        list = list.filter((q) => q.destinationType === 'rfe_page');
        break;
    }
    return list;
  }, [search, filter]);

  const handleCopyUrl = useCallback(async (entry: PermanentQrEntry) => {
    const fullUrl = `https://${entry.permanentUrl}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      Swal.fire({
        title: 'URL copiada',
        html: `<p style="font-size:12px;color:#6b7280"><code style="background:#f3f4f6;padding:2px 6px;border-radius:4px;font-size:11px">${fullUrl}</code></p>`,
        icon: 'success',
        timer: 1800,
        showConfirmButton: false,
        customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', htmlContainer: 'text-sm' },
      });
    } catch {
      Swal.fire({
        title: 'Copiado manual',
        html: `<p style="font-size:12px;word-break:break-all;color:#374151">${fullUrl}</p>`,
        icon: 'info',
        customClass: {
          popup: 'rounded-2xl',
          title: 'font-bold text-sm',
          confirmButton:
            'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50',
        },
        buttonsStyling: false,
      });
    }
  }, []);

  const handleDownloadPng = useCallback((entry: PermanentQrEntry) => {
    Swal.fire({
      title: 'Descargar QR como PNG',
      html: `<p style="font-size:12px;color:#6b7280">Simulando descarga de <strong>qr-${entry.redirectCode}.png</strong></p><p style="font-size:10px;color:#9ca3af;margin-top:4px">Resolucion: 1024 x 1024 px</p>`,
      icon: 'success',
      timer: 2000,
      showConfirmButton: false,
      customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', htmlContainer: 'text-sm' },
    });
  }, []);

  const handleDownloadSvg = useCallback((entry: PermanentQrEntry) => {
    Swal.fire({
      title: 'Descargar QR como SVG',
      html: `<p style="font-size:12px;color:#6b7280">Simulando descarga de <strong>qr-${entry.redirectCode}.svg</strong></p><p style="font-size:10px;color:#9ca3af;margin-top:4px">Formato vectorial escalable</p>`,
      icon: 'success',
      timer: 2000,
      showConfirmButton: false,
      customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', htmlContainer: 'text-sm' },
    });
  }, []);

  const filterOptions: { key: FilterKey; label: string; icon: string }[] = [
    { key: 'all', label: 'Todos', icon: 'ri-layout-grid-line' },
    { key: 'has_domain', label: 'Con dominio', icon: 'ri-global-line' },
    { key: 'no_domain', label: 'Sin dominio', icon: 'ri-link-unlink' },
    { key: 'custom_domain', label: 'Dominio propio', icon: 'ri-shield-check-line' },
    { key: 'rfw_subdomain', label: 'Subdominio RFW', icon: 'ri-router-line' },
    { key: 'rfe_page', label: 'Perfil RFW', icon: 'ri-radio-line' },
  ];

  const plansOrder = ['Empresarial', 'Oro', 'Pro', 'Básico'];
  const sortedFiltered = [...filtered].sort(
    (a, b) => plansOrder.indexOf(a.plan) - plansOrder.indexOf(b.plan),
  );

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1400px] mx-auto">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">
                Dashboard
              </Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">QR Permanente</span>
            </div>

            {/* Page header */}
            <div>
              <h2 className="text-foreground-950 font-bold text-lg">QR Permanente</h2>
              <p className="text-foreground-400 text-xs mt-0.5">
                Cada radio tiene un QR unico que nunca cambia, sin importar dominio, plan o frecuencia
              </p>
            </div>

            {/* Permanent QR Info Banner */}
            <div className="bg-accent-50 border border-accent-200 rounded-2xl p-5 flex flex-col sm:flex-row items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0">
                <i className="ri-shield-keyhole-line text-accent-500 text-lg"></i>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-accent-800 text-sm font-bold mb-1.5">Como funciona el QR Permanente</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-white flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-accent-600 text-[10px] font-bold">1</span>
                    </span>
                    <p className="text-accent-600/80 text-[11px] leading-relaxed">
                      El QR siempre apunta a <strong className="text-accent-700">radiofrecuenciaweb.com/r/codigo</strong>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-white flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-accent-600 text-[10px] font-bold">2</span>
                    </span>
                    <p className="text-accent-600/80 text-[11px] leading-relaxed">
                      El sistema redirige al destino configurado: dominio propio o perfil RFW
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-white flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-accent-600 text-[10px] font-bold">3</span>
                    </span>
                    <p className="text-accent-600/80 text-[11px] leading-relaxed">
                      Aunque cambie dominio, plan o frecuencia, <strong className="text-accent-700">el QR sigue funcionando</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <div className="bg-background-50 border border-background-200 rounded-2xl p-4">
                <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1">Total Radios</p>
                <p className="text-foreground-950 text-xl font-bold">{permanentQrData.length}</p>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-2xl p-4">
                <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1">QR Activos</p>
                <p className="text-emerald-600 text-xl font-bold">{permanentQrData.filter((q) => q.qrStatus === 'active').length}</p>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-2xl p-4">
                <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1">Escaneos Totales</p>
                <p className="text-foreground-950 text-xl font-bold">{totalScans.toLocaleString()}</p>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-2xl p-4">
                <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1">Con Dominio</p>
                <p className="text-emerald-600 text-xl font-bold">{withDomain}</p>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-2xl p-4">
                <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1">Dominio Propio</p>
                <p className="text-accent-600 text-xl font-bold">{permanentQrData.filter((q) => q.domainType === 'custom_domain').length}</p>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-2xl p-4">
                <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1">Sin Dominio</p>
                <p className="text-foreground-400 text-xl font-bold">{withoutDomain}</p>
              </div>
            </div>

            {/* Search + Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1 max-w-md">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-300 text-sm"></i>
                <input
                  type="text"
                  placeholder="Buscar por nombre, frecuencia, codigo o dominio..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
                />
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {filterOptions.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setFilter(opt.key)}
                    className={`flex items-center gap-1 px-3 py-2 rounded-xl text-[11px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      filter === opt.key
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-50 border border-background-200 text-foreground-500 hover:border-foreground-300'
                    }`}
                  >
                    <i className={`${opt.icon} text-[10px]`}></i>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Results count */}
            <p className="text-foreground-400 text-[10px]">
              Mostrando {sortedFiltered.length} de {permanentQrData.length} radios
            </p>

            {/* Table */}
            <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-background-200">
                      <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Radio</th>
                      <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Plan</th>
                      <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Codigo QR</th>
                      <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">URL Permanente</th>
                      <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Dominio</th>
                      <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Redirige a</th>
                      <th className="text-center px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Escaneos</th>
                      <th className="text-center px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedFiltered.map((entry) => (
                      <tr
                        key={entry.id}
                        className="border-b border-background-100 hover:bg-background-100/50 transition-colors"
                      >
                        {/* Radio */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg overflow-hidden shrink-0 bg-background-100">
                              <img
                                src={entry.logoUrl}
                                alt={entry.radioName}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="min-w-0">
                              <p className="text-foreground-800 text-xs font-bold truncate">{entry.radioName}</p>
                              <p className="text-foreground-400 text-[10px]">{entry.frequency}</p>
                            </div>
                          </div>
                        </td>
                        {/* Plan */}
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-bold ${
                              entry.plan === 'Empresarial'
                                ? 'bg-accent-100 text-accent-700'
                                : entry.plan === 'Oro'
                                ? 'bg-amber-100 text-amber-700'
                                : entry.plan === 'Pro'
                                ? 'bg-secondary-100 text-secondary-700'
                                : 'bg-background-200 text-foreground-500'
                            }`}
                          >
                            {entry.plan}
                          </span>
                        </td>
                        {/* Codigo QR */}
                        <td className="px-4 py-3">
                          <code className="text-foreground-800 text-xs font-mono bg-background-100 px-2 py-1 rounded-md">
                            {entry.redirectCode}
                          </code>
                        </td>
                        {/* URL Permanente */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 min-w-0 max-w-[260px]">
                            <code className="text-foreground-600 text-[11px] font-mono truncate">
                              {entry.permanentUrl}
                            </code>
                            <button
                              onClick={() => handleCopyUrl(entry)}
                              className="shrink-0 w-6 h-6 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                              title="Copiar URL"
                            >
                              <i className="ri-file-copy-line text-foreground-400 text-[10px]"></i>
                            </button>
                          </div>
                        </td>
                        {/* Dominio */}
                        <td className="px-4 py-3">
                          {entry.hasDomain && entry.currentDomain ? (
                            <div className="flex items-center gap-1.5">
                              {entry.domainType === 'custom_domain' ? (
                                <i className="ri-shield-check-line text-emerald-500 text-xs"></i>
                              ) : (
                                <i className="ri-router-line text-foreground-400 text-xs"></i>
                              )}
                              <span className="text-foreground-600 text-[11px] truncate max-w-[140px]">{entry.currentDomain}</span>
                            </div>
                          ) : (
                            <span className="text-foreground-300 text-[11px]">—</span>
                          )}
                        </td>
                        {/* Redirige a */}
                        <td className="px-4 py-3">
                          <span className="text-foreground-500 text-[11px]">{entry.destinationLabel}</span>
                        </td>
                        {/* Escaneos */}
                        <td className="px-4 py-3 text-center">
                          <span className="text-foreground-600 text-xs font-semibold">
                            {entry.lastScanCount.toLocaleString()}
                          </span>
                        </td>
                        {/* Acciones */}
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => setPreviewQr(entry)}
                              className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                              title="Ver QR"
                            >
                              <i className="ri-qr-code-line text-foreground-500 text-xs"></i>
                            </button>
                            <button
                              onClick={() => handleCopyUrl(entry)}
                              className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                              title="Copiar URL"
                            >
                              <i className="ri-file-copy-line text-foreground-500 text-xs"></i>
                            </button>
                            <button
                              onClick={() => handleDownloadPng(entry)}
                              className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                              title="Descargar PNG"
                            >
                              <i className="ri-download-line text-foreground-500 text-xs"></i>
                            </button>
                            <Link
                              to={`/sa/radios/${entry.radioId}/qr`}
                              className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                              title="Gestionar QR"
                            >
                              <i className="ri-settings-3-line text-foreground-500 text-xs"></i>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {sortedFiltered.length === 0 && (
                <div className="py-16 text-center">
                  <div className="w-12 h-12 rounded-xl bg-background-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-qr-code-line text-foreground-300 text-xl"></i>
                  </div>
                  <p className="text-foreground-500 text-xs font-medium">Sin resultados</p>
                  <p className="text-foreground-300 text-[10px] mt-0.5">Prueba con otro filtro o termino de busqueda</p>
                </div>
              )}
            </div>

            {/* QR Preview Modal */}
            {previewQr && (
              <>
                <div
                  className="fixed inset-0 z-[80] bg-black/40"
                  onClick={() => setPreviewQr(null)}
                ></div>
                <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
                  <div className="bg-background-50 rounded-2xl border border-background-200 w-full max-w-md p-6 shadow-lg">
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0">
                          <img
                            src={previewQr.logoUrl}
                            alt={previewQr.radioName}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <h3 className="text-foreground-950 font-bold text-sm">{previewQr.radioName}</h3>
                          <p className="text-foreground-400 text-[10px]">{previewQr.frequency}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setPreviewQr(null)}
                        className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                      >
                        <i className="ri-close-line text-foreground-500"></i>
                      </button>
                    </div>

                    {/* QR Image */}
                    <div className="w-48 h-48 mx-auto mb-5 rounded-2xl bg-white border border-background-200 p-3 flex items-center justify-center overflow-hidden">
                      <img
                        src="https://readdy.ai/api/search-image?query=Minimalist%20QR%20code%20illustration%20on%20pure%20white%20background%2C%20elegant%20clean%20modern%20technology%20design%2C%20dark%20geometric%20pattern%20with%20subtle%20brand%20frame%20border%2C%20professional%20corporate%20style%2C%20high%20contrast%20monochrome%2C%20digital%20identity%20element%20isolated&width=400&height=400&seq=sa-permanent-qr-modal-code&orientation=squarish"
                        alt={`QR Permanente ${previewQr.permanentUrl}`}
                        className="w-full h-full object-contain"
                      />
                    </div>

                    {/* URL Display */}
                    <div className="bg-background-100 rounded-xl p-3 mb-4">
                      <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1">URL Permanente</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-foreground-800 text-xs font-mono break-all">
                          https://{previewQr.permanentUrl}
                        </code>
                        <button
                          onClick={() => handleCopyUrl(previewQr)}
                          className="shrink-0 w-8 h-8 rounded-lg bg-foreground-950 text-background-50 flex items-center justify-center cursor-pointer hover:bg-foreground-800 transition-colors"
                        >
                          <i className="ri-file-copy-line text-xs"></i>
                        </button>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex items-center gap-3 text-[10px] text-foreground-400 mb-4">
                      <div className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        <span>{previewQr.lastScanCount.toLocaleString()} escaneos</span>
                      </div>
                      <div className="w-1 h-1 rounded-full bg-foreground-300"></div>
                      <div className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        <span>Desde {previewQr.createdAt}</span>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleCopyUrl(previewQr)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-file-copy-line text-xs"></i>
                        Copiar URL
                      </button>
                      <button
                        onClick={() => handleDownloadPng(previewQr)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-700 text-xs font-bold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-download-line text-xs"></i>
                        PNG
                      </button>
                      <button
                        onClick={() => handleDownloadSvg(previewQr)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-700 text-xs font-bold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-download-line text-xs"></i>
                        SVG
                      </button>
                    </div>

                    {/* Permanent notice */}
                    <div className="mt-4 p-3 rounded-xl bg-accent-50 border border-accent-100 flex items-start gap-2">
                      <i className="ri-information-line text-accent-500 text-xs mt-0.5"></i>
                      <p className="text-accent-600/80 text-[10px] leading-relaxed">
                        Este QR es <strong className="text-accent-700">permanente</strong>. La URL{' '}
                        <code className="bg-white px-1 rounded text-[9px]">r/{previewQr.redirectCode}</code> nunca cambia.
                        Aunque la radio cambie de dominio, plan o frecuencia, el QR seguira funcionando sin interrupcion.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}